import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import LoadingSpinner from '../../components/LoadingSpinner';
import Icon from '../../components/AppIcon';
import orderTrackingService from '../../services/orderTrackingService';
import { adminOrderService } from '../../services/adminOrderService';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';

/**
 * Delivery Confirmation System
 * Comprehensive proof-of-delivery management and customer verification
 */
const DeliveryConfirmationSystem = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [deliveries, setDeliveries] = useState([]);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [filter, setFilter] = useState('all'); // all, pending, confirmed, disputed
  const [searchTerm, setSearchTerm] = useState('');
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadDeliveries();
  }, []);

  const loadDeliveries = async () => {
    try {
      setLoading(true);
      
      // Get all orders with shipments
      const orders = await adminOrderService?.getAllOrders();
      
      // Get shipments and delivery confirmations for each order
      const deliveriesData = [];
      
      for (const order of orders) {
        const shipments = await orderTrackingService?.getOrderShipments(order?.id);
        
        for (const shipment of shipments) {
          if (shipment?.status === 'delivered') {
            const confirmation = await orderTrackingService?.getDeliveryConfirmation(shipment?.id);
            
            deliveriesData?.push({
              orderId: order?.id,
              orderTotal: order?.totalAmount,
              shipment,
              confirmation,
              shippingAddress: order?.shippingAddress
            });
          }
        }
      }
      
      setDeliveries(deliveriesData);
    } catch (error) {
      console.error('Error loading deliveries:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitFeedback = async () => {
    if (!selectedDelivery || rating === 0) return;

    try {
      setSubmitting(true);
      await orderTrackingService?.submitDeliveryFeedback(
        selectedDelivery?.shipment?.id,
        rating,
        feedback
      );
      
      setShowFeedbackModal(false);
      setRating(0);
      setFeedback('');
      setSelectedDelivery(null);
      
      // Reload deliveries
      await loadDeliveries();
    } catch (error) {
      console.error('Error submitting feedback:', error);
      alert('Geri bildirim gönderilirken hata oluştu');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Beklemede', className: 'bg-yellow-100 text-yellow-800' },
      confirmed: { label: 'Onaylandı', className: 'bg-green-100 text-green-800' },
      disputed: { label: 'İtiraz Var', className: 'bg-red-100 text-red-800' },
      resolved: { label: 'Çözüldü', className: 'bg-blue-100 text-blue-800' }
    };

    const config = statusConfig?.[status] || statusConfig?.pending;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${config?.className}`}>
        {config?.label}
      </span>
    );
  };

  const filteredDeliveries = deliveries?.filter(delivery => {
    const matchesFilter = filter === 'all' || delivery?.confirmation?.status === filter;
    const matchesSearch = 
      delivery?.shipment?.trackingNumber?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      delivery?.orderId?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const stats = {
    total: deliveries?.length,
    pending: deliveries?.filter(d => !d?.confirmation || d?.confirmation?.status === 'pending')?.length,
    confirmed: deliveries?.filter(d => d?.confirmation?.status === 'confirmed')?.length,
    disputed: deliveries?.filter(d => d?.confirmation?.status === 'disputed')?.length,
    avgRating: deliveries?.filter(d => d?.confirmation?.customerRating)
      ?.reduce((sum, d) => sum + d?.confirmation?.customerRating, 0) / 
      deliveries?.filter(d => d?.confirmation?.customerRating)?.length || 0
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="main-content bg-background min-h-screen">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
            <LoadingSpinner />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="main-content bg-background min-h-screen">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">
              Teslimat Onay Sistemi
            </h1>
            <p className="text-muted-foreground">
              Teslim edilen siparişlerin doğrulama ve müşteri geri bildirimleri
            </p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
            <div className="bg-card rounded-xl shadow-organic p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Toplam Teslimat</p>
                  <p className="text-2xl font-bold text-foreground">{stats?.total}</p>
                </div>
                <Icon name="Package" size={32} className="text-primary" />
              </div>
            </div>

            <div className="bg-card rounded-xl shadow-organic p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Bekleyen</p>
                  <p className="text-2xl font-bold text-yellow-600">{stats?.pending}</p>
                </div>
                <Icon name="Clock" size={32} className="text-yellow-600" />
              </div>
            </div>

            <div className="bg-card rounded-xl shadow-organic p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Onaylanan</p>
                  <p className="text-2xl font-bold text-green-600">{stats?.confirmed}</p>
                </div>
                <Icon name="CheckCircle" size={32} className="text-green-600" />
              </div>
            </div>

            <div className="bg-card rounded-xl shadow-organic p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">İtirazlı</p>
                  <p className="text-2xl font-bold text-red-600">{stats?.disputed}</p>
                </div>
                <Icon name="AlertCircle" size={32} className="text-red-600" />
              </div>
            </div>

            <div className="bg-card rounded-xl shadow-organic p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Ort. Puan</p>
                  <p className="text-2xl font-bold text-foreground">
                    {stats?.avgRating?.toFixed(1) || '0.0'}
                  </p>
                </div>
                <Icon name="Star" size={32} className="text-yellow-500" />
              </div>
            </div>
          </div>

          {/* Filters and Search */}
          <div className="bg-card rounded-xl shadow-organic p-6 mb-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Icon 
                    name="Search" 
                    size={20} 
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" 
                  />
                  <input
                    type="text"
                    placeholder="Takip numarası veya sipariş ID ile ara..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e?.target?.value)}
                    className="w-full pl-10 pr-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                {['all', 'pending', 'confirmed', 'disputed']?.map(filterOption => (
                  <button
                    key={filterOption}
                    onClick={() => setFilter(filterOption)}
                    className={`px-4 py-2 rounded-lg font-medium transition-smooth ${
                      filter === filterOption
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    {filterOption === 'all' ? 'Tümü' : 
                     filterOption === 'pending' ? 'Bekleyen' :
                     filterOption === 'confirmed' ? 'Onaylanan' : 'İtirazlı'}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Deliveries List */}
          <div className="space-y-4">
            {filteredDeliveries?.length === 0 ? (
              <div className="bg-card rounded-xl shadow-organic p-12 text-center">
                <Icon name="Package" size={48} className="mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">Teslimat kaydı bulunamadı</p>
              </div>
            ) : (
              filteredDeliveries?.map((delivery) => (
                <DeliveryCard
                  key={delivery?.shipment?.id}
                  delivery={delivery}
                  onViewDetails={(d) => setSelectedDelivery(d)}
                  onProvideFeedback={(d) => {
                    setSelectedDelivery(d);
                    setShowFeedbackModal(true);
                  }}
                  getStatusBadge={getStatusBadge}
                />
              ))
            )}
          </div>
        </div>
      </div>

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <FeedbackModal
          delivery={selectedDelivery}
          rating={rating}
          setRating={setRating}
          feedback={feedback}
          setFeedback={setFeedback}
          onSubmit={handleSubmitFeedback}
          onClose={() => {
            setShowFeedbackModal(false);
            setRating(0);
            setFeedback('');
            setSelectedDelivery(null);
          }}
          submitting={submitting}
        />
      )}

      {/* Delivery Details Modal */}
      {selectedDelivery && !showFeedbackModal && (
        <DeliveryDetailsModal
          delivery={selectedDelivery}
          onClose={() => setSelectedDelivery(null)}
          getStatusBadge={getStatusBadge}
        />
      )}
    </>
  );
};

// Delivery Card Component
const DeliveryCard = ({ delivery, onViewDetails, onProvideFeedback, getStatusBadge }) => {
  const hasConfirmation = delivery?.confirmation;
  const hasFeedback = delivery?.confirmation?.customerRating;

  return (
    <div className="bg-card rounded-xl shadow-organic p-6 hover:shadow-lg transition-smooth">
      <div className="flex flex-col md:flex-row gap-6">
        {/* Left: Delivery Info */}
        <div className="flex-1">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-lg font-bold text-foreground mb-1">
                Sipariş #{delivery?.orderId?.slice(0, 8)}
              </h3>
              <p className="text-sm text-muted-foreground">
                Takip No: {delivery?.shipment?.trackingNumber}
              </p>
            </div>
            {hasConfirmation && getStatusBadge(delivery?.confirmation?.status)}
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Kargo Firması</p>
              <p className="text-sm font-medium text-foreground">
                {delivery?.shipment?.carrier?.name || 'Belirtilmemiş'}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Teslimat Tarihi</p>
              <p className="text-sm font-medium text-foreground">
                {delivery?.shipment?.actualDeliveryAt
                  ? format(new Date(delivery?.shipment?.actualDeliveryAt), 'dd MMM yyyy HH:mm', { locale: tr })
                  : 'Belirtilmemiş'}
              </p>
            </div>
          </div>

          {hasConfirmation && (
            <div className="bg-muted/50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="User" size={16} className="text-muted-foreground" />
                <p className="text-sm font-medium text-foreground">
                  Teslim Alan: {delivery?.confirmation?.recipientName}
                </p>
              </div>
              {delivery?.confirmation?.deliveryNotes && (
                <p className="text-sm text-muted-foreground">
                  {delivery?.confirmation?.deliveryNotes}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Right: Photos and Actions */}
        <div className="md:w-64">
          {hasConfirmation && delivery?.confirmation?.deliveryPhotoUrls?.length > 0 && (
            <div className="mb-4">
              <img
                src={delivery?.confirmation?.deliveryPhotoUrls?.[0]}
                alt="Teslimat fotoğrafı"
                className="w-full h-40 object-cover rounded-lg"
              />
              {delivery?.confirmation?.deliveryPhotoUrls?.length > 1 && (
                <p className="text-xs text-muted-foreground mt-2 text-center">
                  +{delivery?.confirmation?.deliveryPhotoUrls?.length - 1} fotoğraf daha
                </p>
              )}
            </div>
          )}

          {hasFeedback && (
            <div className="mb-4 bg-yellow-50 rounded-lg p-3">
              <div className="flex items-center gap-1 mb-1">
                {[1, 2, 3, 4, 5]?.map(star => (
                  <Icon
                    key={star}
                    name="Star"
                    size={16}
                    className={star <= delivery?.confirmation?.customerRating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}
                  />
                ))}
              </div>
              {delivery?.confirmation?.customerFeedback && (
                <p className="text-xs text-gray-700">
                  {delivery?.confirmation?.customerFeedback}
                </p>
              )}
            </div>
          )}

          <div className="space-y-2">
            <button
              onClick={() => onViewDetails(delivery)}
              className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-smooth flex items-center justify-center gap-2"
            >
              <Icon name="Eye" size={16} />
              Detayları Gör
            </button>
            {!hasFeedback && (
              <button
                onClick={() => onProvideFeedback(delivery)}
                className="w-full px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-smooth flex items-center justify-center gap-2"
              >
                <Icon name="MessageSquare" size={16} />
                Geri Bildirim Ver
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Feedback Modal Component
const FeedbackModal = ({ delivery, rating, setRating, feedback, setFeedback, onSubmit, onClose, submitting }) => {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl shadow-2xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-foreground">Teslimat Değerlendirmesi</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <Icon name="X" size={24} />
          </button>
        </div>

        <div className="mb-6">
          <p className="text-sm text-muted-foreground mb-2">
            Takip No: {delivery?.shipment?.trackingNumber}
          </p>
          <p className="text-sm text-muted-foreground">
            Teslimat deneyiminizi değerlendirin
          </p>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-foreground mb-3">
            Puanınız
          </label>
          <div className="flex gap-2 justify-center">
            {[1, 2, 3, 4, 5]?.map(star => (
              <button
                key={star}
                onClick={() => setRating(star)}
                className="transition-smooth hover:scale-110"
              >
                <Icon
                  name="Star"
                  size={32}
                  className={star <= rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}
                />
              </button>
            ))}
          </div>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-foreground mb-2">
            Yorumunuz (İsteğe Bağlı)
          </label>
          <textarea
            value={feedback}
            onChange={(e) => setFeedback(e?.target?.value)}
            placeholder="Teslimat hakkında düşüncelerinizi paylaşın..."
            rows={4}
            className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none"
          />
        </div>

        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-smooth"
            disabled={submitting}
          >
            İptal
          </button>
          <button
            onClick={onSubmit}
            disabled={rating === 0 || submitting}
            className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-smooth disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Gönderiliyor...' : 'Gönder'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Delivery Details Modal Component
const DeliveryDetailsModal = ({ delivery, onClose, getStatusBadge }) => {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-card rounded-xl shadow-2xl max-w-3xl w-full p-6 my-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-foreground">Teslimat Detayları</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <Icon name="X" size={24} />
          </button>
        </div>

        <div className="space-y-6">
          {/* Order Info */}
          <div className="bg-muted/50 rounded-lg p-4">
            <h3 className="text-sm font-bold text-foreground mb-3">Sipariş Bilgileri</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Sipariş ID</p>
                <p className="text-sm font-medium text-foreground">{delivery?.orderId?.slice(0, 8)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Toplam Tutar</p>
                <p className="text-sm font-medium text-foreground">
                  {delivery?.orderTotal?.toFixed(2)} TL
                </p>
              </div>
            </div>
          </div>

          {/* Shipment Info */}
          <div className="bg-muted/50 rounded-lg p-4">
            <h3 className="text-sm font-bold text-foreground mb-3">Kargo Bilgileri</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Takip Numarası</p>
                <p className="text-sm font-medium text-foreground">{delivery?.shipment?.trackingNumber}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Kargo Firması</p>
                <p className="text-sm font-medium text-foreground">{delivery?.shipment?.carrier?.name}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Teslimat Tarihi</p>
                <p className="text-sm font-medium text-foreground">
                  {delivery?.shipment?.actualDeliveryAt
                    ? format(new Date(delivery?.shipment?.actualDeliveryAt), 'dd MMMM yyyy HH:mm', { locale: tr })
                    : 'Belirtilmemiş'}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Durum</p>
                {getStatusBadge(delivery?.confirmation?.status || 'pending')}
              </div>
            </div>
          </div>

          {/* Delivery Confirmation */}
          {delivery?.confirmation && (
            <div className="bg-muted/50 rounded-lg p-4">
              <h3 className="text-sm font-bold text-foreground mb-3">Teslimat Onayı</h3>
              <div className="space-y-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Teslim Alan</p>
                  <p className="text-sm font-medium text-foreground">{delivery?.confirmation?.recipientName}</p>
                </div>
                {delivery?.confirmation?.deliveryNotes && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Notlar</p>
                    <p className="text-sm text-foreground">{delivery?.confirmation?.deliveryNotes}</p>
                  </div>
                )}
                {delivery?.confirmation?.deliveryPhotoUrls?.length > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">Teslimat Fotoğrafları</p>
                    <div className="grid grid-cols-3 gap-2">
                      {delivery?.confirmation?.deliveryPhotoUrls?.map((url, index) => (
                        <img
                          key={index}
                          src={url}
                          alt={`Teslimat fotoğrafı ${index + 1}`}
                          className="w-full h-32 object-cover rounded-lg"
                        />
                      ))}
                    </div>
                  </div>
                )}
                {delivery?.confirmation?.gpsLatitude && delivery?.confirmation?.gpsLongitude && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">GPS Konumu</p>
                    <p className="text-sm text-foreground">
                      {delivery?.confirmation?.gpsLatitude?.toFixed(6)}, {delivery?.confirmation?.gpsLongitude?.toFixed(6)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Customer Feedback */}
          {delivery?.confirmation?.customerRating && (
            <div className="bg-yellow-50 rounded-lg p-4">
              <h3 className="text-sm font-bold text-foreground mb-3">Müşteri Değerlendirmesi</h3>
              <div className="flex items-center gap-1 mb-2">
                {[1, 2, 3, 4, 5]?.map(star => (
                  <Icon
                    key={star}
                    name="Star"
                    size={20}
                    className={star <= delivery?.confirmation?.customerRating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}
                  />
                ))}
                <span className="ml-2 text-sm font-medium text-foreground">
                  {delivery?.confirmation?.customerRating}/5
                </span>
              </div>
              {delivery?.confirmation?.customerFeedback && (
                <p className="text-sm text-gray-700">{delivery?.confirmation?.customerFeedback}</p>
              )}
            </div>
          )}

          {/* Shipping Address */}
          {delivery?.shippingAddress && (
            <div className="bg-muted/50 rounded-lg p-4">
              <h3 className="text-sm font-bold text-foreground mb-3">Teslimat Adresi</h3>
              <div className="space-y-2">
                <p className="text-sm text-foreground">
                  <span className="font-medium">{delivery?.shippingAddress?.full_name}</span>
                </p>
                <p className="text-sm text-foreground">{delivery?.shippingAddress?.address}</p>
                <p className="text-sm text-foreground">{delivery?.shippingAddress?.city}</p>
                <p className="text-sm text-foreground">{delivery?.shippingAddress?.phone}</p>
              </div>
            </div>
          )}
        </div>

        <div className="mt-6">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-smooth"
          >
            Kapat
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeliveryConfirmationSystem;